import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Dumbbell, Calendar, Clock, ChevronRight, Scale } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import SendDietModal from '../components/SendDietModal';

const AthleteDetailPage = () => {
  const pathParts = window.location.pathname.split('/');
  const athleteId = pathParts[3];

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workouts');
  const [showDietModal, setShowDietModal] = useState(false);

  useEffect(() => {
    Promise.all([fetchStats(), fetchWorkouts()]).finally(() => setLoading(false));
  }, [athleteId]);

  const fetchStats = async () => {
    try {
      const res = await apiFetch(`/api/coach/athlete/${athleteId}/stats`);
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWorkouts = async () => {
    try {
      const res = await apiFetch(`/api/coach/athlete/${athleteId}/workouts`);
      if (res.ok) setWorkouts(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;
  if (!stats) return (
    <div className="page-container">
      <div className="empty-state">
        <h3 className="empty-title">{t('athlete_detail.not_found')}</h3>
        <button className="btn btn-primary" onClick={() => navigate('/coach-dashboard')}>{t('common.back')}</button>
      </div>
    </div>
  );

  return (
    <div className="page-container animate-fade-in">
      <div className="header">
        <div>
          <button className="btn btn-sm btn-secondary mb-2" onClick={() => navigate('/coach-dashboard')}>
            {t('common.back')}
          </button>
          <h1 className="header-title">{stats.name}</h1>
          <p className="text-muted" style={{ fontSize: '13px' }}>{stats.email}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className="notif-btn"
            onClick={() => setShowDietModal(true)}
            title={t('diets.send_title', { name: stats.name.split(' ')[0] })}
          >
            <Scale size={20} />
          </button>
          <div style={{
            width: '52px', height: '52px', borderRadius: '50%',
            background: 'rgba(var(--primary-rgb),0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '24px', flexShrink: 0
          }}>
            {stats.picture
              ? <img src={stats.picture} alt="" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover' }} />
              : '🧑‍🏋️'
            }
          </div>
        </div>
      </div>

      {showDietModal && (
        <SendDietModal
          athleteId={athleteId}
          athleteName={stats.name}
          onClose={() => setShowDietModal(false)}
          onSent={() => {}}
        />
      )}

      <div className="stats-grid mb-4">
        <div className="stat-card">
          <div style={{ color: 'var(--primary)', marginBottom: '4px' }}><Dumbbell size={20} /></div>
          <div className="stat-value">{stats.total_workouts}</div>
          <div className="stat-label">{t('athlete_detail.stat_routines')}</div>
        </div>
        <div className="stat-card">
          <div style={{ color: 'var(--primary)', marginBottom: '4px' }}><Calendar size={20} /></div>
          <div className="stat-value">{stats.total_sessions}</div>
          <div className="stat-label">{t('athlete_detail.stat_sessions')}</div>
        </div>
      </div>

      {stats.last_session_date && (
        <div className="card mb-4" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Clock size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <div>
            <p style={{ fontSize: '12px', color: 'var(--muted-foreground)' }}>{t('athlete_detail.last_session')}</p>
            <p style={{ fontWeight: '600', color: 'var(--foreground)' }}>{stats.last_session_date}</p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {['workouts'].map(tab => (
          <button
            key={tab}
            className={`day-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {t('athlete_detail.tab_routines')}
          </button>
        ))}
      </div>

      <button
        className="btn btn-primary w-full mb-4"
        onClick={() => navigate(`/coach/athlete/${athleteId}/create-workout`)}
      >
        <Plus size={18} />
        {t('athlete_detail.create_routine_btn', { name: stats.name.split(' ')[0] })}
      </button>

      {workouts.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
          <h3 className="empty-title">{t('athlete_detail.no_routines_title')}</h3>
          <p className="empty-desc">{t('athlete_detail.no_routines_desc')}</p>
        </div>
      ) : (
        workouts.map(workout => (
          <div
            key={workout.workout_id}
            className="card mb-3"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/coach/athlete/${athleteId}/workout/${workout.workout_id}`)}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontWeight: '600', color: 'var(--foreground)', marginBottom: '4px' }}>
                  {workout.name}
                </p>
                {workout.description && (
                  <p style={{ fontSize: '13px', color: 'var(--muted-foreground)' }}>{workout.description}</p>
                )}
                <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '4px' }}>
                  {t('athlete_detail.days_count', { count: workout.days?.length || 0 })}
                </p>
              </div>
              <ChevronRight size={18} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default AthleteDetailPage;
